/**
 * postMessage protocol with the parent loader.
 *
 * The iframe MUST validate that inbound messages come from `window.parent`
 * (not nested frames or popups) AND that their structural shape is right.
 *
 * About origin pinning: this iframe is sandboxed without `allow-same-origin`,
 * so its effective origin is the spec-defined opaque string `"null"`. The
 * loader's outbound messages target `'*'` (intentional — see loader.ts
 * `sendToIframe`), which means the iframe's `event.origin` is reported as
 * `"null"` for every message from the parent. We *do* record the first
 * init's `event.origin` for parity with the source check, but in practice
 * `e.origin === lockedParentOrigin` reduces to `"null" === "null"` — the
 * REAL gate is `e.source === window.parent`. Anyone scripting a malicious
 * embed of our iframe could pass the origin check; only the source check
 * (and downstream backend `allowedOrigins` validation at handshake time)
 * provides actual security. Don't add code that relies on the origin lock
 * as a second factor.
 */

import { decodeStringsOverride, type EmbedStrings } from './strings';

/**
 * Tools declaradas pelo parent (rune-bi/frontend, etc.) via
 * `RuneAgent.push({ tools })`. O loader strips a função `run` antes de
 * postar pro iframe (funções não atravessam postMessage); apenas a
 * definição vem aqui. Iframe envia este array no body de cada POST
 * `/widget/chat` — o backend valida + injeta no LLM. Quando o LLM chama
 * uma tool, iframe posta `tool-call-request` pro parent, loader executa o
 * `run` correspondente e responde via `tool-call-response`.
 *
 * Caps duplicados no backend (validateStrictSchema, max 20, name regex,
 * description ≤ 500, schema ≤ 4 KB, total ≤ 30 KB) — defesa em
 * profundidade contra um parent malicioso (XSS na página do customer).
 */
export type ToolDefinition = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  autoRun: boolean;
};

// Permissive enough pra aceitar camelCase (`listAuroraAlerts`) e snake_case
// (`open_kb_doc`). Mirrors o que OpenAI/Anthropic aceitam pra tool names.
const TOOL_NAME_RE = /^[a-zA-Z0-9_-]{1,64}$/;
const TOOL_DESCRIPTION_MAX = 500;
const TOOLS_MAX = 20;

export type EndUserInfo = {
  id: string;
  name?: string;
  email?: string;
};

/** Plano-15 IV — assinatura do bundle (top-level, cobre id E/OU metadata). */
export type IdentitySignature = { exp: number; signature: string };

const SIGNATURE_RE = /^[a-f0-9]{64}$/;

export type ParentInit = {
  hostOrigin: string;
  tools?: ToolDefinition[];
  // Optional UI string overrides (i18n). Customers running the widget on a
  // non-PT-BR site pass a partial map here; unknown keys are silently
  // ignored so older customers don't break when we add new strings.
  strings?: Partial<EmbedStrings>;
  /** Visual theme id; iframe maps to a CSS data-theme on its root. */
  theme?: string;
  /** Identidade do visitante (vinda do customer no `RuneAgent.push`). Repassada
   *  ao backend no handshake `/widget/session`. Não-verificada (plano-15). */
  endUser?: EndUserInfo;
  /** Tags free-form pra filtrar no admin /conversations. Repassadas verbatim. */
  customerMetadata?: Record<string, string>;
  /** Plano-15 IV — assinatura do bundle de identidade (top-level). */
  identitySignature?: IdentitySignature;
};

const META_MAX_KEYS = 20;
const META_MAX_KEY_LEN = 64;
const META_MAX_VALUE_LEN = 256;
const END_USER_ID_MAX = 128;
const END_USER_NAME_MAX = 256;
const END_USER_EMAIL_MAX = 256;

const THEME_RE = /^[a-z][a-z0-9-]{0,31}$/;

/**
 * Parent → iframe response carrying the result of a customer-side handler
 * execution. Either `output` (success) or `error` (failure / timeout / no
 * handler). Validated structurally: `output` is opaque (we forward whatever
 * the customer returned to the LLM); `error` is a small enum of strings so
 * the chat panel can render a useful message.
 */
export type ToolCallResponse =
  | { toolCallId: string; output: unknown; error?: never }
  | { toolCallId: string; error: string; output?: never };

/**
 * Per-turn live context fornecido pelo host. Diferente de `customerMetadata`
 * (que vai pro JWT/DDB e fica oculto da LLM), `liveContext` entra como uma
 * mensagem de usuário sintética (`[CONTEXT]\n…`) imediatamente antes do turno
 * do usuário — fica fora do system prompt pra não competir com guardrails.
 * Cap 4096 chars; vazio/null limpa.
 */
export type LiveContextUpdate = { value: string };

export const LIVE_CONTEXT_MAX_LEN = 4096;

export type ParentInbound =
  | { type: 'agents/init'; payload: ParentInit }
  | { type: 'agents/open' }
  | { type: 'agents/close' }
  | { type: 'agents/tool-call-response'; payload: ToolCallResponse }
  | { type: 'agents/live-context-update'; payload: LiveContextUpdate };

/**
 * Iframe → parent. `tool-call-request` carries enough info for the loader
 * to look up the handler in `window.RuneAgent.push(...)`'s `handlers` map
 * and run it. `autoRun` is informational only: the loader executes either
 * way; the iframe owns the confirm UX (rendered before posting). Including
 * it helps debugging / future logging on the parent side.
 */
export type ToolCallRequest = {
  toolCallId: string;
  name: string;
  input: unknown;
  autoRun: boolean;
};

export type IframeOutbound =
  | { type: 'agents/ready' }
  | { type: 'agents/close-request' }
  | { type: 'agents/resize'; payload: { fullscreen: boolean } }
  | { type: 'agents/tool-call-request'; payload: ToolCallRequest };

export function isFromParent(e: MessageEvent): boolean {
  return e.source !== null && e.source === window.parent && window.parent !== window;
}

function decodeTools(raw: unknown): ToolDefinition[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: ToolDefinition[] = [];
  for (const t of raw) {
    if (out.length >= TOOLS_MAX) break;
    if (!t || typeof t !== 'object') continue;
    const r = t as {
      name?: unknown;
      description?: unknown;
      inputSchema?: unknown;
      autoRun?: unknown;
    };
    if (typeof r.name !== 'string' || !TOOL_NAME_RE.test(r.name)) continue;
    if (
      typeof r.description !== 'string' ||
      r.description.length === 0 ||
      r.description.length > TOOL_DESCRIPTION_MAX
    )
      continue;
    if (!r.inputSchema || typeof r.inputSchema !== 'object' || Array.isArray(r.inputSchema))
      continue;
    if (typeof r.autoRun !== 'boolean') continue;
    out.push({
      name: r.name,
      description: r.description,
      inputSchema: r.inputSchema as Record<string, unknown>,
      autoRun: r.autoRun,
    });
  }
  return out.length > 0 ? out : undefined;
}

function decodeEndUser(raw: unknown): EndUserInfo | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as {
    id?: unknown;
    name?: unknown;
    email?: unknown;
  };
  if (typeof r.id !== 'string' || r.id.length === 0 || r.id.length > END_USER_ID_MAX) {
    return undefined;
  }
  const out: EndUserInfo = { id: r.id };
  if (typeof r.name === 'string' && r.name.length > 0 && r.name.length <= END_USER_NAME_MAX) {
    out.name = r.name;
  }
  if (typeof r.email === 'string' && r.email.length > 0 && r.email.length <= END_USER_EMAIL_MAX) {
    out.email = r.email;
  }
  return out;
}

/**
 * Plano-15 IV — decodifica a assinatura do bundle (top-level). Só aceita o par
 * exp+signature válido; qualquer lado inválido descarta os dois (o backend
 * rejeitaria como signature_invalid). Não derruba endUser/metadata.
 */
function decodeIdentitySignature(raw: unknown): IdentitySignature | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as { exp?: unknown; signature?: unknown };
  if (
    typeof r.exp === 'number' &&
    Number.isInteger(r.exp) &&
    r.exp > 0 &&
    r.exp < 2_000_000_000 &&
    typeof r.signature === 'string' &&
    SIGNATURE_RE.test(r.signature)
  ) {
    return { exp: r.exp, signature: r.signature };
  }
  return undefined;
}

function decodeCustomerMetadata(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, string> = {};
  let kept = 0;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (kept >= META_MAX_KEYS) break;
    if (typeof k !== 'string' || k.length === 0 || k.length > META_MAX_KEY_LEN) continue;
    if (typeof v !== 'string' || v.length === 0 || v.length > META_MAX_VALUE_LEN) continue;
    out[k] = v;
    kept += 1;
  }
  return kept > 0 ? out : undefined;
}

function decodeToolCallResponse(raw: unknown): ToolCallResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { toolCallId?: unknown; output?: unknown; error?: unknown };
  if (typeof r.toolCallId !== 'string' || r.toolCallId.length === 0) return null;
  if (typeof r.error === 'string' && r.error.length > 0) {
    return { toolCallId: r.toolCallId, error: r.error };
  }
  if (Object.prototype.hasOwnProperty.call(r, 'output')) {
    return { toolCallId: r.toolCallId, output: r.output };
  }
  return null;
}

export function decodeInbound(data: unknown): ParentInbound | null {
  if (!data || typeof data !== 'object') return null;
  const m = data as Partial<ParentInbound> & { payload?: unknown };
  if (m.type === 'agents/init') {
    const p = m.payload as Partial<ParentInit> | undefined;
    if (p && typeof p.hostOrigin === 'string' && p.hostOrigin.length > 0) {
      const rawTheme = (p as { theme?: unknown }).theme;
      const theme =
        typeof rawTheme === 'string' && THEME_RE.test(rawTheme) ? rawTheme : undefined;
      return {
        type: 'agents/init',
        payload: {
          hostOrigin: p.hostOrigin,
          tools: decodeTools((p as { tools?: unknown }).tools),
          strings: decodeStringsOverride((p as { strings?: unknown }).strings),
          theme,
          endUser: decodeEndUser((p as { endUser?: unknown }).endUser),
          customerMetadata: decodeCustomerMetadata(
            (p as { customerMetadata?: unknown }).customerMetadata,
          ),
          identitySignature: decodeIdentitySignature(
            (p as { identitySignature?: unknown }).identitySignature,
          ),
        },
      };
    }
    return null;
  }
  if (m.type === 'agents/open' || m.type === 'agents/close') {
    return { type: m.type };
  }
  if (m.type === 'agents/tool-call-response') {
    const payload = decodeToolCallResponse(m.payload);
    if (payload) return { type: 'agents/tool-call-response', payload };
    return null;
  }
  if (m.type === 'agents/live-context-update') {
    const p = m.payload as { value?: unknown } | undefined;
    if (!p || typeof p.value !== 'string') return null;
    // Cap inbound — mirror backend Zod limit so a misbehaving parent can't
    // pump a multi-MB string through the iframe → backend pipeline. Trim
    // silently rather than reject; backend re-validates.
    const value = p.value.length > LIVE_CONTEXT_MAX_LEN
      ? p.value.slice(0, LIVE_CONTEXT_MAX_LEN)
      : p.value;
    return { type: 'agents/live-context-update', payload: { value } };
  }
  return null;
}

export function postToParent(msg: IframeOutbound, parentOrigin: string): void {
  // parentOrigin = '*' is forbidden — caller must have done init first. The
  // ready signal pre-init can target '*' since we don't know who the parent
  // is yet (anti-eavesdropping via targetOrigin is moot here — `ready` carries
  // no secret and merely advertises that we're alive).
  window.parent?.postMessage(msg, parentOrigin || '*');
}
